// Product quality tier detection. Maps the device down to an engine DSP tier and a
// visuals tier. Uses a quick warmup CPU probe + coarse-pointer/viewport heuristics
// (NOT UA strings).
import type { AppTier, QualityTier, VisualQualityTier } from '@shared/voice';

export interface ResolvedTier {
  app: AppTier;
  engine: QualityTier;
  visual: VisualQualityTier;
  reducedMotion: boolean;
}

/** Rough single-core throughput probe: time a tight float loop. Returns ms. */
function cpuProbeMs(): number {
  const N = 4096 * 8;
  const start = performance.now();
  let acc = 0;
  for (let iter = 0; iter < 8; iter++) {
    for (let i = 0; i < N; i++) {
      // a cheap nonlinear op the JIT can't fold away
      acc += Math.sin(i * 0.001 + acc) * 0.5;
    }
  }
  // prevent dead-code elimination
  if (acc === 12345.6789) console.debug(acc);
  return performance.now() - start;
}

export function resolveTier(): ResolvedTier {
  const reducedMotion =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const coarse = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  const small = typeof window !== 'undefined' && window.innerWidth < 900;
  const probe = cpuProbeMs();

  let app: AppTier;
  if ((coarse && small) || probe > 6) {
    app = 'mobile';
  } else if (probe > 2.5) {
    app = 'desktop-mid';
  } else {
    app = 'desktop-high';
  }

  const engine: QualityTier = app === 'mobile' ? 'mobile' : 'desktop';
  const visual: VisualQualityTier =
    app === 'mobile' ? 'low' : app === 'desktop-mid' ? 'medium' : 'high';

  return { app, engine, visual, reducedMotion };
}
